import { Avatar, Box, Center, Container, Flex, SkeletonText, Spinner, Table, Text, useDisclosure } from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import TitleBar from "../../components/UI/TitleBar"
import { userHasPermission } from "../../utils"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { NoPermission } from "../../components/UI/no-permission"
import { z } from "zod"
import { CategoriesOrderBy } from "../../client"
import { useQuery } from "@tanstack/react-query"
import Sort from "../../components/Common/Sort"
import ActionsMenu from "../../components/Common/ActionsMenu"
import Navbar from "../../components/Common/Navbar"
import { PaginationFooter } from "../../components/Common/PaginationFooter"
import { CategoriesTableProps, readCategories } from "../../api/categories"

const DetailCategory = lazy(() => import("../../components/Categories/DetailCategory"))
const AddCategory = lazy(() => import("../../components/Categories/AddCategory"))
const PER_PAGE = 25

const categoriesSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/categories")({
  component: Categories,
  validateSearch: (search) => categoriesSearchSchema.parse(search),
})


function CategoriesTable({searchValue, cg_update, cg_delete}: CategoriesTableProps) {
  const fields = [
    { title: "Name", col: "name", align: undefined },
    { title: "Status", col: "is_active", align: undefined },
    { title: "Bearbeitet von", col: null, align: undefined },
    { title: "ID", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) => navigate({
    search: (prev: { [key: string]: string }) => ({ ...prev, page }),
  })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<CategoriesOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readCategories({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: categories,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: CategoriesOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  // Open category detail view
  const detailModal = useDisclosure()
  const [categoryId, setCategoryId] = useState("")

  const openModal = (id: string) => {
    setCategoryId(id)
    detailModal.onOpen()
  }

  return (
    <>
      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailCategory
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            categoryId={categoryId}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    onClick={() => sortTable(field.col as CategoriesOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                    key={i}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader textAlign={field.align} key={field.title}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(5).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!categories?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    Keine Kategorien verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                categories?.data.map((category) => (
                  <Table.Row key={category.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(category.id)} cursor="pointer">
                      {category.name}
                    </Table.Cell>

                     <Table.Cell onClick={() => openModal(category.id)} cursor="pointer">
                      <Flex gap={2}>
                        <Box
                          w="2"
                          h="2"
                          borderRadius="50%"
                          bg={category.is_active ? "success" : "danger"}
                          alignSelf="center"
                        />
                        {category.is_active ? "Aktiv" : "Inaktiv"}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(category.id)} cursor="pointer">
                      <Flex alignItems="center">
                        <Avatar.Root mr={2}>
                          <Avatar.Fallback
                            name={
                              category.fk_last_edited_by.lastname && category.fk_last_edited_by.firstname
                                ? `${category.fk_last_edited_by.lastname} ${category.fk_last_edited_by.firstname}`
                                : category.fk_last_edited_by.firstname || category.fk_last_edited_by.lastname || category.fk_last_edited_by.email
                            }
                          />
                          <Avatar.Image src={category.fk_last_edited_by.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                        </Avatar.Root>
                        <Text>{category.fk_last_edited_by.email}</Text>
                      </Flex>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(category.id)} cursor="pointer">
                      {category.id}
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Kategorie"
                        value={category}
                        disableEdit={!cg_update}
                        disableDelete={!cg_delete}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>

      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={categories?.count ? categories?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

export default CategoriesTable

function Categories() {
  // Permission check
  const [cg_create, cg_read, cg_update, cg_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `categories_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!cg_read) return <NoPermission/>

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  return (
    <Container maxW="full">
      <TitleBar title="Kategorien" />
      <Navbar
        type="Kategorie"
        addAllowed={cg_create}
        addModalAs={AddCategory}
        showSearch={true}
        onSearchChange={handleSearchValueChange}
      />
      <CategoriesTable
        cg_update={cg_update}
        searchValue={searchValue}
        cg_delete={cg_delete}
      />
    </Container>
  )
}
